import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import './BackgroundCheckPage.css';

const BackgroundCheckPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    current_position: '',
    linkedin_profile: '',
  });
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile) => {
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(selectedFile.type)) {
        setErrors(prev => ({ ...prev, file: 'Please upload a PDF or DOCX file' }));
        return;
      }

      if (selectedFile.size > maxSize) {
        setErrors(prev => ({ ...prev, file: 'File size must be less than 10MB' }));
        return;
      }

      setFile(selectedFile);
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (formData.phone.trim().length < 10) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.current_position.trim()) {
      newErrors.current_position = 'Current position is required';
    }

    if (!file) {
      newErrors.file = 'Resume is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('full_name', formData.full_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('current_position', formData.current_position);
      if (formData.linkedin_profile) {
        submitData.append('linkedin_profile', formData.linkedin_profile);
      }
      submitData.append('resume', file);

      await api.submitBackgroundCheck(submitData);

      toast.success('Background check request submitted successfully!');
      setSubmitted(true);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      current_position: '',
      linkedin_profile: '',
    });
    setFile(null);
    setSubmitted(false);
    setErrors({});
  };

  if (submitted) {
    return (
      <div className="background-check-page" data-testid="background-check-page">
        <div className="container">
          <div className="success-container" data-testid="success-message">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            <h2>Request Submitted Successfully!</h2>
            <p>
              Thank you for submitting your background check request. We have received your information and resume.
              Our team will review your submission and contact you within 2-3 business days.
            </p>
            <div className="success-details">
              <p><strong>Name:</strong> {formData.full_name}</p>
              <p><strong>Email:</strong> {formData.email}</p>
              <p><strong>Position:</strong> {formData.current_position}</p>
            </div>
            <button className="btn btn-primary" onClick={handleReset} data-testid="submit-another-btn">
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="background-check-page" data-testid="background-check-page">
      {/* Hero Section */}
      <section className="bgcheck-hero">
        <div className="container">
          <div className="bgcheck-hero-content">
            <h1 data-testid="bgcheck-hero-title">Background Check Request</h1>
            <p data-testid="bgcheck-hero-subtitle">
              Submit your information for a comprehensive background verification. All data is handled securely and confidentially.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="bgcheck-form-section section">
        <div className="container">
          <div className="bgcheck-form-container">
            <form onSubmit={handleSubmit} className="bgcheck-form" data-testid="background-check-form">
              {/* Personal Information */}
              <div className="form-section">
                <h3 className="form-section-title">Personal Information</h3>
                
                <div className="form-group">
                  <label htmlFor="full_name" className="form-label">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    className={`form-input ${errors.full_name ? 'error' : ''}`}
                    placeholder="John Doe"
                    data-testid="input-full-name"
                  />
                  {errors.full_name && (
                    <span className="form-error" data-testid="error-full-name">
                      <AlertCircle size={14} /> {errors.full_name}
                    </span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder="john.doe@example.com"
                      data-testid="input-email"
                    />
                    {errors.email && (
                      <span className="form-error" data-testid="error-email">
                        <AlertCircle size={14} /> {errors.email}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input ${errors.phone ? 'error' : ''}`}
                      placeholder="+1 (234) 567-8900"
                      data-testid="input-phone"
                    />
                    {errors.phone && (
                      <span className="form-error" data-testid="error-phone">
                        <AlertCircle size={14} /> {errors.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="form-section">
                <h3 className="form-section-title">Professional Information</h3>
                
                <div className="form-group">
                  <label htmlFor="current_position" className="form-label">
                    Current Position <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="current_position"
                    name="current_position"
                    value={formData.current_position}
                    onChange={handleInputChange}
                    className={`form-input ${errors.current_position ? 'error' : ''}`}
                    placeholder="Software Engineer"
                    data-testid="input-position"
                  />
                  {errors.current_position && (
                    <span className="form-error" data-testid="error-position">
                      <AlertCircle size={14} /> {errors.current_position}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="linkedin_profile" className="form-label">
                    LinkedIn Profile (Optional)
                  </label>
                  <input
                    type="url"
                    id="linkedin_profile"
                    name="linkedin_profile"
                    value={formData.linkedin_profile}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="https://linkedin.com/in/johndoe"
                    data-testid="input-linkedin"
                  />
                </div>
              </div>

              {/* Resume Upload */}
              <div className="form-section">
                <h3 className="form-section-title">Resume Upload</h3>
                
                <div className="form-group">
                  <label className="form-label">
                    Upload Resume <span className="required">*</span>
                  </label>
                  <div
                    className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${errors.file ? 'error' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    data-testid="file-upload-area"
                  >
                    <input
                      type="file"
                      id="resume"
                      accept=".pdf,.docx,.doc"
                      onChange={handleFileChange}
                      className="file-input"
                      data-testid="file-input"
                    />
                    <label htmlFor="resume" className="file-upload-label">
                      {file ? (
                        <div className="file-selected">
                          <FileText size={48} />
                          <p className="file-name">{file.name}</p>
                          <p className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      ) : (
                        <div className="file-prompt">
                          <Upload size={48} />
                          <p className="file-prompt-text">Drag and drop your resume here, or click to browse</p>
                          <p className="file-prompt-hint">Supported formats: PDF, DOCX (Max 10MB)</p>
                        </div>
                      )}
                    </label>
                  </div>
                  {errors.file && (
                    <span className="form-error" data-testid="error-file">
                      <AlertCircle size={14} /> {errors.file}
                    </span>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-accent btn-lg"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <div className="spinner-small"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </form>

            {/* Info Sidebar */}
            <div className="bgcheck-info">
              <h3>What to Expect</h3>
              <ul>
                <li>
                  <CheckCircle size={20} />
                  <span>Secure and confidential processing</span>
                </li>
                <li>
                  <CheckCircle size={20} />
                  <span>Response within 2-3 business days</span>
                </li>
                <li>
                  <CheckCircle size={20} />
                  <span>Comprehensive verification</span>
                </li>
                <li>
                  <CheckCircle size={20} />
                  <span>GDPR & compliance standards met</span>
                </li>
              </ul>

              <div className="info-note">
                <AlertCircle size={20} />
                <p>
                  <strong>Note:</strong> All information provided will be handled in accordance with our privacy policy and applicable data protection regulations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BackgroundCheckPage;
